import { useEffect, useState } from "react";
import { useParams, useSearchParams, Link } from "react-router-dom";
import axios from "axios";
import { motion } from "framer-motion";
import { Loader2, Check, Bell, BellOff, ArrowLeft } from "lucide-react";
import { toast } from "sonner";
import { API } from "@/lib/api";
import { useI18n } from "@/i18n";

const C = { bg: "#0B0D12", surface: "#151923", mint: "#55E6B3", coral: "#FF6B6B" };

const T = {
  en: { title: "Manage preferences", sub: "Choose what {name} sends you.", future: "Future updates", future_desc: "New private releases and drops from this artist.", unsub: "Unsubscribe from everything", unsub_desc: "Stop all emails from this release. You can still return with your private link.", save: "Save preferences", saved: "Preferences saved.", save_err: "Could not save. Try again.", invalid: "This preferences link is invalid or has expired.", back: "Back to the release", on: "On", off: "Off" },
  es: { title: "Gestionar preferencias", sub: "Elige qué te envía {name}.", future: "Novedades futuras", future_desc: "Nuevos lanzamientos privados y drops de este artista.", unsub: "Darme de baja de todo", unsub_desc: "Deja de recibir todos los emails de este lanzamiento. Podrás volver con tu enlace privado.", save: "Guardar preferencias", saved: "Preferencias guardadas.", save_err: "No se pudo guardar. Inténtalo de nuevo.", invalid: "Este enlace de preferencias es inválido o ha caducado.", back: "Volver al lanzamiento", on: "Activado", off: "Desactivado" },
  pt: { title: "Gerenciar preferências", sub: "Escolha o que {name} envia para você.", future: "Novidades futuras", future_desc: "Novos lançamentos privados e drops deste artista.", unsub: "Cancelar tudo", unsub_desc: "Pare de receber todos os emails deste lançamento. Você ainda pode voltar com seu link privado.", save: "Salvar preferências", saved: "Preferências salvas.", save_err: "Não foi possível salvar. Tente de novo.", invalid: "Este link de preferências é inválido ou expirou.", back: "Voltar ao lançamento", on: "Ligado", off: "Desligado" },
  fr: { title: "Gérer les préférences", sub: "Choisissez ce que {name} vous envoie.", future: "Actualités futures", future_desc: "Nouvelles sorties privées et drops de cet artiste.", unsub: "Me désabonner de tout", unsub_desc: "Arrêtez tous les emails de cette sortie. Vous pourrez revenir avec votre lien privé.", save: "Enregistrer les préférences", saved: "Préférences enregistrées.", save_err: "Impossible d'enregistrer. Réessayez.", invalid: "Ce lien de préférences est invalide ou a expiré.", back: "Retour à la sortie", on: "Activé", off: "Désactivé" },
  de: { title: "Einstellungen verwalten", sub: "Wähle, was {name} dir sendet.", future: "Zukünftige Updates", future_desc: "Neue private Veröffentlichungen und Drops dieses Artists.", unsub: "Von allem abmelden", unsub_desc: "Stoppe alle E-Mails dieser Veröffentlichung. Du kannst mit deinem privaten Link zurückkehren.", save: "Einstellungen speichern", saved: "Einstellungen gespeichert.", save_err: "Konnte nicht gespeichert werden. Versuch es erneut.", invalid: "Dieser Einstellungslink ist ungültig oder abgelaufen.", back: "Zurück zur Veröffentlichung", on: "An", off: "Aus" },
  it: { title: "Gestisci preferenze", sub: "Scegli cosa ti invia {name}.", future: "Novità future", future_desc: "Nuove uscite private e drop di questo artista.", unsub: "Annulla tutto", unsub_desc: "Interrompi tutte le email di questa uscita. Puoi tornare con il tuo link privato.", save: "Salva preferenze", saved: "Preferenze salvate.", save_err: "Impossibile salvare. Riprova.", invalid: "Questo link delle preferenze non è valido o è scaduto.", back: "Torna all'uscita", on: "Attivo", off: "Disattivo" },
  ko: { title: "환경설정 관리", sub: "{name}이(가) 보내는 내용을 선택하세요.", future: "향후 소식", future_desc: "이 아티스트의 새로운 비공개 릴리스와 드롭.", unsub: "모든 수신 거부", unsub_desc: "이 릴리스의 모든 이메일을 중단합니다. 개인 링크로 언제든 다시 올 수 있어요.", save: "환경설정 저장", saved: "환경설정이 저장되었습니다.", save_err: "저장하지 못했습니다. 다시 시도하세요.", invalid: "이 환경설정 링크가 유효하지 않거나 만료되었습니다.", back: "릴리스로 돌아가기", on: "켜짐", off: "꺼짐" },
};

function Toggle({ on, onClick, testid }) {
  return (
    <button type="button" onClick={onClick} data-testid={testid}
      className="relative w-12 h-7 rounded-full shrink-0 transition-colors"
      style={{ background: on ? C.mint : "rgba(255,255,255,0.16)" }} aria-pressed={on}>
      <span className="absolute top-1 w-5 h-5 rounded-full bg-white transition-all" style={{ left: on ? "26px" : "4px" }} />
    </button>
  );
}

export default function ManagePreferences() {
  const { slug } = useParams();
  const { lang } = useI18n();
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const tr = (k, vars) => {
    let s = (T[lang] || T.en)[k] || T.en[k] || k;
    if (vars) Object.keys(vars).forEach((v) => (s = s.replace(`{${v}}`, vars[v])));
    return s;
  };

  const [loading, setLoading] = useState(true);
  const [invalid, setInvalid] = useState(false);
  const [saving, setSaving] = useState(false);
  const [info, setInfo] = useState(null);
  const [future, setFuture] = useState(false);
  const [unsub, setUnsub] = useState(false);

  useEffect(() => {
    (async () => {
      if (!token) { setInvalid(true); setLoading(false); return; }
      try {
        const { data } = await axios.get(`${API}/prefs/${token}`);
        setInfo(data);
        setFuture(!!data.future_updates);
        setUnsub(!!data.unsubscribed);
      } catch (e) { setInvalid(true); }
      finally { setLoading(false); }
    })();
  }, [token]);

  const save = async () => {
    setSaving(true);
    try {
      await axios.post(`${API}/prefs/${token}`, { future_updates: future, unsubscribed: unsub });
      toast.success(tr("saved"));
    } catch (e) { toast.error(tr("save_err")); }
    finally { setSaving(false); }
  };

  const brandName = info?.artist || info?.project_name || "this artist";

  return (
    <div className="min-h-[100dvh] flex items-center justify-center px-4 py-16" style={{ background: C.bg }} data-testid="manage-preferences">
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.5 }}
        className="w-full max-w-md rounded-2xl p-7 sm:p-8" style={{ background: C.surface, border: "1px solid rgba(255,255,255,0.08)" }}>
        {loading ? (
          <div className="flex items-center justify-center gap-2 py-10 text-white/60"><Loader2 className="w-5 h-5 animate-spin" /></div>
        ) : invalid ? (
          <div className="text-center py-6" data-testid="prefs-invalid">
            <BellOff className="w-8 h-8 mx-auto mb-4" style={{ color: C.coral }} />
            <p className="text-white/80">{tr("invalid")}</p>
          </div>
        ) : (
          <>
            <p className="font-mono-x text-[11px] tracking-[0.4em] uppercase mb-2" style={{ color: C.mint }}>{info?.project_name}</p>
            <h1 className="font-display text-white text-3xl font-bold tracking-tight mb-1">{tr("title")}</h1>
            <p className="text-white/55 text-sm mb-2">{tr("sub", { name: brandName })}</p>
            {info?.email && <p className="font-mono-x text-[11px] text-white/35 mb-7 break-all">{info.email}</p>}

            <div className="space-y-5">
              <div className="flex items-start justify-between gap-4 rounded-xl p-4" style={{ background: "rgba(255,255,255,0.03)" }}>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <Bell className="w-4 h-4" style={{ color: C.mint }} />
                    <span className="text-white font-semibold text-sm">{tr("future")}</span>
                  </div>
                  <p className="text-white/45 text-xs leading-relaxed">{tr("future_desc")}</p>
                </div>
                <Toggle on={future && !unsub} onClick={() => { setFuture((v) => !v); }} testid="prefs-future-toggle" />
              </div>

              <div className="flex items-start justify-between gap-4 rounded-xl p-4" style={{ background: "rgba(255,255,255,0.03)" }}>
                <div className="flex-1">
                  <div className="flex items-center gap-2 mb-1">
                    <BellOff className="w-4 h-4" style={{ color: C.coral }} />
                    <span className="text-white font-semibold text-sm">{tr("unsub")}</span>
                  </div>
                  <p className="text-white/45 text-xs leading-relaxed">{tr("unsub_desc")}</p>
                </div>
                <Toggle on={unsub} onClick={() => { setUnsub((v) => { const nv = !v; if (nv) setFuture(false); return nv; }); }} testid="prefs-unsub-toggle" />
              </div>
            </div>

            <button onClick={save} disabled={saving} data-testid="prefs-save"
              className="mt-7 w-full flex items-center justify-center gap-2 font-semibold rounded-full py-3.5 transition-transform hover:scale-[1.02] active:scale-95 disabled:opacity-60"
              style={{ background: C.mint, color: "#04120c" }}>
              {saving ? <Loader2 className="w-5 h-5 animate-spin" /> : <><Check className="w-4 h-4" /> {tr("save")}</>}
            </button>

            <Link to={`/${info?.slug || slug}`} data-testid="prefs-back"
              className="mt-5 flex items-center justify-center gap-1.5 font-mono-x text-[11px] tracking-widest uppercase text-white/40 hover:text-white transition-colors">
              <ArrowLeft className="w-3.5 h-3.5" /> {tr("back")}
            </Link>
          </>
        )}
      </motion.div>
    </div>
  );
}
